import React, { useState, useCallback } from "react";
import { useSelector, useDispatch, Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import styled from "styled-components";
import { Button, message } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import CustomModal from "@/components/modal";
import { 
  SUBSCRIPTION_PLAN_TYPES, 
  SUBSCRIPTION_PLANS, 
  getSubscriptionPlanUI,
} from "@/constants/subscription";
import * as ReactDOM from "react-dom/client";
import mockImage from "@/assets/images/mock.png";
import { useSubscriptionApi } from "@/api/subscription";
import store from "@/store";
import { subscriptionActions } from "@/store/slices/subscription";


/**
 * Helper function to generate subscription plan data with UI properties and button handlers
 * @param {string} currentPlan - The current subscription plan code
 * @returns {Array} The subscription plans data with UI properties
 */
const generateSubscriptionPlansWithUI = (currentPlan) => {  
  const buttonConfigs = {
    [SUBSCRIPTION_PLAN_TYPES.FREE]: (userPlan) => {
      if (userPlan === SUBSCRIPTION_PLAN_TYPES.FREE) {
        return {
          className: "plan-button current",
          text: "Current Plan",
          onClick: null,
        };
      } else if (
        userPlan === SUBSCRIPTION_PLAN_TYPES.PREMIUM ||
        userPlan === SUBSCRIPTION_PLAN_TYPES.PLATINUM
      ) {
        return {
          className: "plan-button downgrade",
          text: "Downgrade to Free",
          action: "downgrade",
          planCode: "free"
        };
      }
      return null;
    },
    
    [SUBSCRIPTION_PLAN_TYPES.PREMIUM]: (userPlan) => {
      if (userPlan === SUBSCRIPTION_PLAN_TYPES.FREE) {
        return {
          className: "plan-button upgrade",
          text: "Upgrade to Premium",
          planCode: "premium",
          action: "upgrade"
        };
      } else if (userPlan === SUBSCRIPTION_PLAN_TYPES.PREMIUM) {
        return {
          className: "plan-button current",
          text: "Current Plan",
          onClick: null,
        };
      } else if (userPlan === SUBSCRIPTION_PLAN_TYPES.PLATINUM) {
        return {
          className: "plan-button downgrade",
          text: "Downgrade to Premium",
          planCode: "premium",
          action: "downgrade"
        };
      }
      return null;
    },
    
    [SUBSCRIPTION_PLAN_TYPES.PLATINUM]: (userPlan) => {
      if (
        userPlan === SUBSCRIPTION_PLAN_TYPES.FREE ||
        userPlan === SUBSCRIPTION_PLAN_TYPES.PREMIUM
      ) {
        return {
          className: "plan-button upgrade",
          text: "Upgrade to Unlock All Features",
          planCode: "platinum",
          action: "upgrade"
        };
      } else if (userPlan === SUBSCRIPTION_PLAN_TYPES.PLATINUM) {
        return {
          className: "plan-button current",
          text: "Current Plan",
          onClick: null,
        };
      }
      return null;
    }
  };
  
  // Format features text for Premium and Platinum plans
  const formatFeatures = (plan) => {
    const features = [...plan.features];
    
    if (plan.id === SUBSCRIPTION_PLAN_TYPES.PREMIUM && features.length > 0) {
      features[0] = {
        ...features[0],
        text: (
          <span>
            Everything in <span className="free-text">Free</span> Plan
          </span>
        )
      };
    } else if (plan.id === SUBSCRIPTION_PLAN_TYPES.PLATINUM && features.length > 0) {
      features[0] = {
        ...features[0],
        text: (
          <span>
            Everything in <span className="premium-text">Premium</span> Plan
          </span>
        )
      };
    }
    
    return features;
  };
  
  // Map subscription plans with UI properties
  return SUBSCRIPTION_PLANS.map(plan => {
    const planUI = getSubscriptionPlanUI(plan.id);
    const buttonConfig = buttonConfigs[plan.id];
    
    return {
      ...plan,
      ...planUI,
      getButtonProps: buttonConfig,
      features: formatFeatures(plan)
    };
  });
};

/**
 * Reusable component for subscription plan cards
 * @param {Object} props - Component props
 * @param {Object} props.plan - The subscription plan data
 * @param {string} props.currentPlan - The current subscription plan code
 * @returns {JSX.Element} The subscription plan card component
 */
const SubscriptionPlanCard = ({ plan, currentPlan }) => {
  const { subscribePlan } = useSubscriptionApi();
  const dispatch = useDispatch();
  
  /**
   * Close the subscription modal by unmounting React components and removing DOM elements
   */
  const closeSubscriptionModal = useCallback(() => {
    try {
      // Use the global modal references for cleanup
      if (modalRoot) {
        modalRoot.unmount();
        modalRoot = null;
      }
      
      if (modalContainer && modalContainer.parentNode) {
        modalContainer.parentNode.removeChild(modalContainer);
        modalContainer = null;
      }
    } catch (error) {
      console.error('Error closing modal:', error);
    }
  }, []);
  
  /**
   * Handle subscription plan change (upgrade or downgrade)
   * @param {string} planCode - The plan code to subscribe to
   * @param {string} action - The action type ('upgrade' or 'downgrade')
   */
  const handleSubscription = useCallback(async (planCode, action) => {    
    try {
      const payload = {
        genericRecord: { code: planCode }
      };
      
      const response = await subscribePlan(payload);

      if (response.status === "ok") {
        if (response.result?.link) {
          window.location.href = response.result.link;
          return;
        }
                
        const actionText = action === "upgrade" ? "subscribed to" : "downgraded to";
        const planName = planCode.charAt(0).toUpperCase() + planCode.slice(1);
        message.success(`Successfully ${actionText} ${planName} plan`);
        
        closeSubscriptionModal();
        dispatch(subscriptionActions.setSubscriptionPlan(planCode.toUpperCase()));
        
        if (window.location.pathname !== '/subscription') {
          setTimeout(() => {
            window.location.href = '/subscription';
          }, 1000);
        }
      } else {
        message.error(`Failed to ${action} plan: ${response.message || 'Unknown error'}`);
        closeSubscriptionModal();
      }
    } catch (error) {
      closeSubscriptionModal();
      console.error(`Error ${action} plan:`, error);
      message.error(`An error occurred while ${action === "upgrade" ? "subscribing to" : "downgrading"} the plan`);
    }
  }, [subscribePlan, dispatch, closeSubscriptionModal]);
  
  const buttonProps = plan.getButtonProps ? plan.getButtonProps(currentPlan) : null;
  
  const handleClick = buttonProps && buttonProps.planCode ? 
    () => handleSubscription(buttonProps.planCode, buttonProps.action) : 
    buttonProps?.onClick;
  
  const isHighlighted = plan.id === SUBSCRIPTION_PLAN_TYPES.PLATINUM;
  const isPopular = plan.id === SUBSCRIPTION_PLAN_TYPES.PLATINUM;
  
  return (
    <div className={`plan-card ${isHighlighted ? 'highlight' : ''}`}>
      {isPopular && <div className="popular-tag">Popular</div>}
      <div className={`plan-header ${plan.id.toLowerCase()}`}>
        <span>{plan.name}</span>
      </div>
      <div className="plan-price-container">
        <div className="plan-price">
          <span className="currency">{plan.currency}</span>
          <span className="amount">{plan.price}</span>
          <span className="period">{plan.period}</span>
        </div>
        <div className="plan-description">{plan.description || 'Basic subscription plan'}</div>
      </div>
      <div className="features-list">
        {plan.features.map((feature, index) => (
          <div key={index} className="feature">
            {feature.available ? (
              <CheckOutlined className="icon check" />
            ) : (
              <CloseOutlined className="icon close" />
            )}
            {feature.text}
          </div>
        ))}
      </div>
      {buttonProps && (
        <Button className={buttonProps.className} onClick={handleClick}>
          {buttonProps.text}
        </Button>
      )}
    </div>
  );
};

/**
 * Reusable component for the subscription plans modal content
 * @param {Object} props - Component props
 * @param {Array} props.plans - The subscription plans data
 * @param {string} props.currentPlan - The current subscription plan code
 * @returns {JSX.Element} The subscription plans modal content component
 */
const SubscriptionPlansModalContent = React.memo(({ plans, currentPlan }) => {
  return (
    <SubscriptionPlansContainer>
      <div className="subscription-comparison">
        {plans.map((plan, index) => (
          <SubscriptionPlanCard 
            key={plan.id || index} 
            plan={plan} 
            currentPlan={currentPlan} 
          />
        ))}
      </div>
    </SubscriptionPlansContainer>
  );
});

// Store reference to the modal root for cleanup
let modalRoot = null;
let modalContainer = null;

/**
 * Safely unmount and clean up the subscription modal
 * @param {Function} callback - Optional callback to execute after cleanup
 */
const cleanupSubscriptionModal = (callback) => {
  try {
    // Clean up by unmounting the React tree and removing the container
    if (modalRoot) {
      modalRoot.unmount();
      modalRoot = null;
    }
    
    if (modalContainer && modalContainer.parentNode) {
      modalContainer.parentNode.removeChild(modalContainer);
      modalContainer = null;
    }
    
    // Execute callback if provided
    if (typeof callback === 'function') callback();
  } catch (error) {
    console.error('Error cleaning up subscription modal:', error);
  }
};

/**
 * The SubscriptionPlansModal component
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {boolean} props.showBackground - Whether to show background image
 */
const SubscriptionPlansModal = ({ onClose, showBackground = true }) => {
  const [isOpen, setIsOpen] = useState(true);
  
  const currentPlan = useSelector(state => state.subscription.plan);
  
  const subscriptionPlansWithUI = generateSubscriptionPlansWithUI(currentPlan);
  
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300); // Wait for animation to complete
  }, [onClose]);
  
  return (
    <>
      {showBackground && <BackgroundMockImage />}
      <div>
        <StyledCustomModal
          title="Subscription Plans"
          open={isOpen}
          onCancel={handleClose}
          destroyOnClose
          footer={null}
          maskClosable={false}
          width="65rem"
          closeIcon={<CloseOutlined />}
          withTitleDivider
        >
          <SubscriptionPlansModalContent 
            plans={subscriptionPlansWithUI} 
            currentPlan={currentPlan}
          />
        </StyledCustomModal>
      </div>
    </>
  );
};

/**
 * Utility function to show the subscription plans modal from anywhere in the app
 * @param {Function} callback - Optional callback to execute after modal is closed
 * @param {Object} options - Optional configuration options
 * @param {boolean} options.showBackground - Whether to show the background image (default: true)
 */
export const showSubscriptionPlansModal = (callback, options = {}) => {
  const { showBackground = true } = options;
  
  try {
    cleanupSubscriptionModal();
    
    modalContainer = document.createElement('div');
    modalContainer.style.position = 'fixed';
    modalContainer.style.zIndex = '1000';
    modalContainer.style.top = '0';
    modalContainer.style.left = '0';
    modalContainer.style.width = '100%';
    modalContainer.style.height = '100%';
    document.body.appendChild(modalContainer);
    
    modalRoot = ReactDOM.createRoot(modalContainer);
    
    modalRoot.render(
      <React.StrictMode>
        <Provider store={store}>
          <BrowserRouter>
            <SubscriptionPlansModal 
              onClose={() => cleanupSubscriptionModal(callback)} 
              showBackground={showBackground} 
            />
          </BrowserRouter>
        </Provider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error('Error showing subscription modal:', error);
    message.error('Failed to open subscription plans');
  }
};

// Background image for the modal
const BackgroundMockImage = styled.div`
  position: fixed;
  top: calc(var(--bo-header-height) + 20px);
  left: var(--bo-spacing-left);
  right: var(--bo-spacing-right);
  bottom: 0;
  background-image: url(${mockImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 1000;
  opacity: 1;
  filter: blur(8px);
  width: calc(100% - var(--bo-spacing-left) - var(--bo-spacing-right));
  height: calc(95% - var(--bo-header-height));
`;

// Styled custom modal
const StyledCustomModal = styled(CustomModal)`
  .ant-modal-mask {
    display: none; /* Hide the default mask since we're using our custom wrapper */
  }

  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
`;

// Styled container for subscription plans
const SubscriptionPlansContainer = styled.div`
.subscription-comparison {
    display: flex;
    gap: 20px;
    padding: 14px;
    overflow-x: auto;
    align-items: flex-end;
  }

  .plan-card {
    flex: 1 1 0%;
    min-width: 280px;
    border-radius: 10px;
    padding: 0px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    text-align: center;
    background-color: white;
    position: relative;
    border: 1px solid rgb(229, 231, 235);
    overflow: hidden;
    min-height: 460px;
    height: 100%;
    flex-direction: column;
    padding: 24px;
    transition: all 0.3s ease;
    color: #253858;

    :hover {
      box-shadow: 0px 6px 12px #0000002a;
      transform: translateY(-5px);

      .plan-header span {
        opacity: 1;
      }
    }
  }

  .plan-card.highlight {
    background-color: #f0fdfc;
    border: 1px solid #7ce2d8;
    min-height: 480px;
    height: 100%;
  }

  .popular-tag {
    position: absolute;
    top: 0;
    right: 0;
    background-color: #7ce2d8;
    color: white;
    border: none;
    border-radius: 0px 10px;
    margin: 0;
    text-align: center;
    font: normal normal medium 14px/21px var(--main-font-base);
    letter-spacing: 0px;
    color: #f8feff;
    padding: 7px 9px;
  }

  .plan-header {
    width: 100%;
    color: white;
    font-weight: 500;
    font-size: 16px;
    text-align: left;
    display: inline-flex;

    span {
      border-radius: 10px;
      padding: 9px 30px;
      opacity: 0.8;
    }
  }

  .plan-header.free span {
    background-color: #95b7b4;
    opacity: 0.6;
  }

  .plan-header.premium span {
    background: transparent linear-gradient(90deg, #acddf4 0%, #1a5cd8 100%) 0%
      0% no-repeat padding-box;
  }

  .plan-header.platinum span {
    background: transparent
      linear-gradient(270deg, #7ce2d8 0%, #687ac7 46%, #b2009d 100%) 0% 0%
      no-repeat padding-box;
  }

  .plan-price-container {
    text-align: left;
  }

  .plan-price {
    display: flex;
    align-items: baseline;
    justify-content: flex-start;
  }

  .currency {
    font-size: var(--font-size-l);
    font-weight: 600;
    margin-right: 4px;
  }

  .amount {
    font: normal normal 600 40px/12px var(--main-font-base);
    font-weight: bold;
  }

  .period {
    font-size: 14px;
    color: #666;
    margin-left: 4px;
  }

  .plan-description {
    margin-bottom: 16px;
    font: normal normal normal var(--font-size-s) / var(--font-size-m)
      var(--main-font-base);
    letter-spacing: 0px;
    color: #b6b6b6;
  }

  .features-list {
    width: 100%;
    text-align: left;
  }

  .feature {
    margin: 12px 0;
    display: flex;
    align-items: center;
    font: normal normal 600 12px/19px var(--main-font-base);
  }

  .icon {
    margin-right: 8px;
  }

  .icon.check {
    color: #009fb2;
  }

  .icon.close {
    color: #ff3318;
  }

  .premium-text {
    color: #5a88dd;
  }
  .free-text {
    color: #95b7b4;
  }

  .plan-button {
    width: 100%;
    height: 48px;
    background-color: #dff7f4;
    border-radius: 10px;
    font: normal normal medium var(--font-size-m) var(--main-font-base);
    letter-spacing: 0px;
    color: #253858;
    border: none;
    box-shadow: none;
    :hover {
      background-color: #7ce2d8 !important;
      color: #253858 !important;
    }
  }

  .plan-button.current,
  .plan-button.current:hover {
    background-color: #727d91 !important;
    color: #e5e7eb !important;
    cursor: default;
  }

  .plan-card:has(.platinum) > .plan-button.upgrade {
    background: transparent linear-gradient(270deg, #dff7f4 0%, #707c7a 100%) 0%
      0% no-repeat padding-box;
    color: #ffffff;
    :hover {
      background: transparent
        linear-gradient(90deg, #c7fff8 0%, #8ff2e9 47%, #64eadd 100%) 0% 0%
        no-repeat padding-box;
      color: #253858;
    }
  }

`;

export default SubscriptionPlansModal;
